import React, { useState } from 'react';
import styled from 'styled-components';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';

const Card = styled.div`
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 8px;
  padding: 25px;
  margin-bottom: 20px;
`;

const CardTitle = styled.h2`
  color: #ff6b00;
  font-size: 1.5rem;
  margin: 0 0 20px 0;
  text-transform: uppercase;
  letter-spacing: 1.5px;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 25px;
`;

const MetricBox = styled.div`
  background: #0a0a0a;
  padding: 15px;
  border-left: 3px solid ${props => props.color || '#ff6b00'};
  border-radius: 4px;
`;

const MetricLabel = styled.div`
  font-size: 0.85rem;
  color: #888;
  margin-bottom: 5px;
`;

const MetricValue = styled.div`
  font-size: 1.8rem;
  color: ${props => props.color || '#fff'};
  font-weight: 700;
`;

const MetricSubtext = styled.div`
  font-size: 0.75rem;
  color: #666;
  margin-top: 5px;
`;

const StatusIndicator = styled.span`
  display: inline-block;
  padding: 3px 10px;
  background: ${props => props.status === 'on-track' ? '#00ff00' : props.status === 'warning' ? '#ffaa00' : '#ff0000'};
  color: #000;
  border-radius: 3px;
  font-size: 0.75rem;
  font-weight: 700;
  margin-left: 10px;
`;

const SemiconductorSupercycleTracker = () => {
  const [currentYear] = useState(new Date().getFullYear());
  const targetYear = 2030;
  const yearsRemaining = targetYear - currentYear;
  const progressPercentage = ((currentYear - 2020) / (targetYear - 2020)) * 100;

  // Semiconductor supercycle progress - tracking revenue toward $1T target
  const generateProgressData = () => {
    const data = [];
    const startYear = 2020;
    const baseRevenue = 440; // $440B in 2020
    const targetRevenue = 1000; // $1T target by 2030

    for (let year = startYear; year <= 2035; year++) {
      const yearsSince = year - startYear;
      const yearsToTarget = targetYear - startYear;

      const expectedRevenue = baseRevenue + ((targetRevenue - baseRevenue) / yearsToTarget) * yearsSince;
      const actualRevenue = year <= currentYear ? baseRevenue * Math.pow(1.22, yearsSince) : null;
      const upperBound = baseRevenue * Math.pow(1.30, yearsSince); // 30% CAGR bull case
      const lowerBound = baseRevenue * Math.pow(1.10, yearsSince); // 10% CAGR bear case

      const item = {
        year,
        expectedRevenue: Math.min(targetRevenue * 1.5, expectedRevenue),
        actualRevenue,
        upperBound,
        lowerBound,
      };
      data.push(item);
    }

    return data;
  };

  const data = generateProgressData();

  // Current semiconductor revenue (est. ~$700B in 2025)
  const currentRevenue = 440 * Math.pow(1.22, currentYear - 2020);
  const expectedRevenue = 440 + ((1000 - 440) / (targetYear - 2020)) * (currentYear - 2020);
  const status = currentRevenue >= expectedRevenue * 0.9 ? 'on-track' :
                 currentRevenue >= expectedRevenue * 0.7 ? 'warning' : 'behind';

  return (
    <Card>
      <CardTitle>
        Semiconductor Supercycle Tracker: Path to $1T+ by 2030
        <StatusIndicator status={status}>
          {status === 'on-track' ? 'ON TRACK' : status === 'warning' ? 'MONITORING' : 'BEHIND'}
        </StatusIndicator>
      </CardTitle>

      <MetricsGrid>
        <MetricBox color="#00ff00">
          <MetricLabel>Years to $1T Target</MetricLabel>
          <MetricValue color="#00ff00">{yearsRemaining}</MetricValue>
          <MetricSubtext>Target: 2030 ($1T+ revenue)</MetricSubtext>
        </MetricBox>

        <MetricBox color="#ff6b00">
          <MetricLabel>Current Industry Revenue</MetricLabel>
          <MetricValue color="#ff6b00">${currentRevenue.toFixed(0)}B</MetricValue>
          <MetricSubtext>vs Expected: ${expectedRevenue.toFixed(0)}B</MetricSubtext>
        </MetricBox>

        <MetricBox color="#00aaff">
          <MetricLabel>Supercycle Progress</MetricLabel>
          <MetricValue color="#00aaff">{progressPercentage.toFixed(1)}%</MetricValue>
          <MetricSubtext>Since 2020 baseline ($440B)</MetricSubtext>
        </MetricBox>

        <MetricBox color="#aa00ff">
          <MetricLabel>AI-Driven CAGR</MetricLabel>
          <MetricValue color="#aa00ff">~22%</MetricValue>
          <MetricSubtext>Actual 2020-2025 growth</MetricSubtext>
        </MetricBox>
      </MetricsGrid>

      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
          <XAxis
            dataKey="year"
            stroke="#888"
            tick={{ fill: '#888' }}
          />
          <YAxis
            stroke="#888"
            tick={{ fill: '#888' }}
            label={{ value: 'Semiconductor Revenue ($B)', angle: -90, position: 'insideLeft', fill: '#888' }}
          />
          <Tooltip
            contentStyle={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: '4px' }}
            labelStyle={{ color: '#ff6b00' }}
            formatter={(value) => `$${value.toFixed(0)}B`}
          />
          <Legend wrapperStyle={{ color: '#888' }} />

          <Line
            type="monotone"
            dataKey="upperBound"
            stroke="#00ff00"
            strokeWidth={1}
            strokeDasharray="5 5"
            dot={false}
            name="Bull Case (30% CAGR)"
          />

          <Line
            type="monotone"
            dataKey="expectedRevenue"
            stroke="#ff6b00"
            strokeWidth={3}
            dot={false}
            name="Expected Path ($1T by 2030)"
          />

          <Line
            type="monotone"
            dataKey="actualRevenue"
            stroke="#00aaff"
            strokeWidth={3}
            dot={{ fill: '#00aaff', r: 4 }}
            name="Actual Revenue (~22% CAGR)"
          />

          <Line
            type="monotone"
            dataKey="lowerBound"
            stroke="#ff0000"
            strokeWidth={1}
            strokeDasharray="5 5"
            dot={false}
            name="Bear Case (10% CAGR)"
          />

          <ReferenceLine
            x={2030}
            stroke="#00ff00"
            strokeWidth={2}
            label={{ value: 'Target: $1T+ (2030)', fill: '#00ff00', position: 'top' }}
          />

          <ReferenceLine
            x={currentYear}
            stroke="#fff"
            strokeWidth={2}
            strokeDasharray="3 3"
            label={{ value: 'Today', fill: '#fff', position: 'top' }}
          />
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
};

export default SemiconductorSupercycleTracker;
